import {
  canAccessWithPermissions,
  getPermissionsFromRoles,
  RoleDefinitions,
} from '@react-admin/ra-rbac';
import { Auth0AuthProvider, httpClient as auth0HttpClient } from 'ra-auth-auth0';
import { Auth0Client } from '@auth0/auth0-spa-js';
import { debounce } from 'lodash';
import { delay } from '../utils';
import Mutex from '../Mutex';
import { HttpError } from 'react-admin';
import { ReactAdminError } from './JsonApiTypes';
import { match } from 'ts-pattern';

const permissions = [
  'admin:api',
  'read:action_logs',
  'read:action_log_changes',
  'read:action_logs_by_users',
  'read:affiliate_networks',
  'read:billing_item_charge_types',
  'read:billing_item_payment_types',
  'read:billing_item_statuses',
  'read:billing_items',
  'read:bids',
  'read:categories',
  'read:clicks',
  'read:clicks_by_merchants',
  'read:clicks_by_referrers',
  'read:clicks_daily_summaries',
  'read:clicks_summaries',
  'read:countries',
  'read:customers',
  'read:feed_fields',
  'read:feed_scans',
  'read:feeds',
  'read:invoices',
  'read:labels',
  'read:label_values',
  'read:manufacturers',
  'read:mapping_errors',
  'read:mapping_rules',
  'read:merchant_remarks',
  'read:merchant_spendings',
  'read:merchant_statuses',
  'read:merchants',
  'read:merchants_offer_summaries',
  'read:offers',
  'read:pay_accounts',
  'read:price_histories',
  'read:products',
  'read:product_labels',
  'read:regions',
  'read:registrations',
  'read:report_types',
  'read:reports',
  'read:scans',
  'read:shop_scans',
  'read:states',
  'read:subscriptions',
  'read:tracked_error_issues',
  'read:tracked_error_occurrences',
  'read:users',
  'write:affiliate_networks',
  'write:bids',
  'write:billing_items',
  'write:categories',
  'write:clicks',
  'write:customers',
  'write:feed_fields',
  'write:feeds',
  'write:invoices',
  'write:labels',
  'write:label_values',
  'write:manufacturers',
  'write:mapping_rules',
  'write:merchant_remarks',
  'write:merchant_statuses',
  'write:merchants',
  'write:offers',
  'write:pay_accounts',
  'write:payments',
  'write:price_histories',
  'write:products',
  'write:product_labels',
  'write:regions',
  'write:registrations',
  'write:reports',
  'write:subscriptions',
  'write:tracked_error_issues',
];

const auth0 = new Auth0Client({
  domain: import.meta.env.VITE_AUTH0_DOMAIN,
  clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
  cacheLocation: 'localstorage',
  authorizationParams: {
    audience: import.meta.env.VITE_AUTH0_AUDIENCE,
  },
});

const auth0AuthProvider = Auth0AuthProvider(auth0, {
  loginRedirectUri: import.meta.env.VITE_LOGIN_REDIRECT_URL,
  logoutRedirectUri: import.meta.env.VITE_LOGOUT_REDIRECT_URL,
  redirectOnCheckAuth: false,
});

const claimsNamespace = 'https://claims.cms.getprice.com.au/permissions';

// Actions taken from https://react-admin-ee.marmelab.com/documentation/ra-rbac#vocabulary
const ReadActions = ['list', 'show', 'export', 'read'];
const WriteActions = ['create', 'edit', 'delete', 'clone', 'write'];

export const roleDefinitions = permissions
  .map((permission: string) => {
    const [action, resource] = permission.split(':');
    return match(action)
      .with('admin', () => ({ [permission]: [{ resource: '*', action: '*' }] }))
      .with('read', () => ({ [permission]: [{ resource, action: ReadActions }] }))
      .with('write', () => ({ [permission]: [{ resource, action: WriteActions }] }))
      .otherwise(() => ({}));
  })
  .reduce((acc, role) => ({ ...acc, ...role }), {} as RoleDefinitions);

export type AuthError = Error & {
  redirectTo?: string; // URL to redirect to after authentication failure
  logoutUser?: boolean; // Whether to log the user out after authentication failure
  message?: string; // Customize the error message
};

const getRolesMutex = new Mutex();

async function getRoles(forceRefresh = false): Promise<string[]> {
  // wrap in mutex to prevent multiple calls to getRoles from concurrently
  // calling auth0.isAuthenticated and auth0.getIdTokenClaims
  return getRolesMutex.withLock(async () => {
    const authenticated = await auth0.isAuthenticated();

    if (!authenticated) {
      return [];
    }

    if (!forceRefresh) {
      const given = JSON.parse(localStorage.getItem('permissions') ?? 'null');
      const assumed = JSON.parse(
        localStorage.getItem('RaStore.preferences.assumedPermissions') ?? 'null',
      );

      if (given === null) {
        // If we don't have any cached permissions, we need to refresh
        const claims = await auth0.getIdTokenClaims();

        const permissions =
          claims && Object.prototype.hasOwnProperty.call(claims, claimsNamespace)
            ? claims[claimsNamespace]
            : [];

        localStorage.setItem('permissions', JSON.stringify(permissions));
      }

      if (assumed === null && given !== null && given.length > 0) {
        localStorage.setItem('RaStore.preferences.assumedPermissions', JSON.stringify(given));
      }

      return assumed?.filter((p: string) => given?.includes(p)) ?? given ?? [];
    }

    const claims = await auth0.getIdTokenClaims();

    const permissions =
      claims && Object.prototype.hasOwnProperty.call(claims, claimsNamespace)
        ? claims[claimsNamespace]
        : [];

    localStorage.setItem('permissions', JSON.stringify(permissions));

    return permissions;
  });
}

export const AuthProvider = {
  ...auth0AuthProvider,

  async canAccess({
    resource,
    action,
    record,
    forceRefresh = false,
  }: {
    resource: string;
    action: string;
    record?: Record<string, unknown>;
    forceRefresh?: boolean;
  }): Promise<boolean> {
    const userRoles = await getRoles(forceRefresh);

    return canAccessWithPermissions({
      permissions: getPermissionsFromRoles({ roleDefinitions, userRoles }),
      action,
      resource,
      record,
    });
  },

  // Checks for a 401 Unauthorized error. If found, redirect to the login page.
  //
  // For 403 Forbidden errors, we assume that the user has insufficient
  // permissions and do not logout the user, as they may still be able to access
  // other resources on the page. We simply want the unauthorized message to
  // pop, so set `logoutUser` to false.
  async checkError(error: HttpError) {
    if (error.status === 401) {
      throw new Error() as AuthError;
    }

    if (error.status === 403) {
      const err = new Error() as AuthError;
      err.logoutUser = false;

      throw err;
    }

    if (error.status === 500) {
      const responseData = error.body as ReactAdminError;

      if (responseData?.apiResponse?.errors?.some((e) => e.status === '401')) {
        throw new Error() as AuthError;
      }

      if (responseData?.apiResponse?.errors?.some((e) => e.status === '403')) {
        const err = new Error() as AuthError;
        err.logoutUser = false;

        throw err;
      }
    }
  },

  // Debounce login to prevent multiple calls in rapid succession from causing
  // multiple redirects to the login page.
  //
  // Also, adds a delay to the login action to allow the browser to see that
  // the login action is still running while the redirect is happening in the
  // background. Previously, this method would return before the redirect
  // happened, causing the browser to allow other actions to happen before the
  // redirect occurred, potentially cancelling the login redirect. See:
  //
  // <https://github.com/marmelab/ra-auth-auth0/issues/18>
  login: debounce(
    async (params) => {
      const result = auth0AuthProvider.login(params);
      await delay(1000);

      return result;
    },
    5000,
    { leading: true },
  ),

  // Debounce logout to prevent multiple calls in rapid succession from causing
  // multiple redirects to the logout page.
  //
  // Also, adds a delay to the logout action to allow the browser to see that
  // the logout action is still running while the redirect is happening in the
  // background. Previously, this method would return before the redirect
  // happened, causing the browser to allow other actions to happen before the
  // redirect occurred, potentially cancelling the logout redirect. See:
  //
  // <https://github.com/marmelab/ra-auth-auth0/issues/18>
  logout: debounce(
    async (
      params: Record<string, unknown>,
    ): ReturnType<ReturnType<typeof Auth0AuthProvider>['logout']> => {
      localStorage.removeItem('permissions');

      const result = auth0AuthProvider.logout(params);
      await delay(1000);

      return result;
    },
    5000,
    { leading: true },
  ),
};

export const httpClient = auth0HttpClient(auth0);
